import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HComboBox,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { IconButton } from './IconButton';
import { useState } from 'react';

type Option<T> = { label: string; value: T };

interface SelectComboBoxProps<T extends string> {
  options: Array<Option<T>>;
  value: T | '';
  onChange: (value: T) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

// Single-value searchable select — the input shows the chosen option's label
// and typing filters the list. For multi-select with free input, use ComboxBox.
export function SelectComboBox<T extends string>({
  options,
  value,
  onChange,
  label,
  placeholder,
  disabled,
  error,
}: SelectComboBoxProps<T>) {
  const [query, setQuery] = useState('');

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed
    ? options.filter((opt) => opt.label.toLowerCase().includes(trimmed))
    : options;

  const selected = options.find((opt) => opt.value === value) ?? null;

  return (
    <div>
      {!!label && (
        <label className="mb-2 font-semibold text-body-medium block">
          {label}
        </label>
      )}
      <HComboBox
        value={selected}
        onChange={(opt: Option<T> | null) => {
          if (opt) onChange(opt.value);
        }}
        onClose={() => setQuery('')}
        by="value"
        disabled={!!disabled}
        immediate
      >
        <div
          className={classNames(
            'group -outline-offset-1 flex flex-col border relative rounded-xs overflow-hidden border-grey-t-600 transition-all duration-100 focus-within:outline-white focus-within:outline-2 bg-background-secondary',
            {
              'border-red-300': error?.length,
              'border-grey-t-700 text-text-disabled': disabled,
              'hover:border-white': !disabled,
            }
          )}
        >
          <div className="relative flex">
            <ComboboxButton className="cursor-pointer group absolute top-1/2 transform right-0 -translate-y-1/2 p-3">
              <ChevronDownIcon
                className={classNames(
                  'size-5 group-data-open:rotate-180 transition-all',
                  { 'stroke-text-disabled': disabled }
                )}
              />
            </ComboboxButton>
            <ComboboxInput
              displayValue={(opt: Option<T> | null) => opt?.label ?? ''}
              className="w-full rounded outline-none disabled:text-text-disabled placeholder:text-text-subtle py-3 leading-none px-3 h-10 pr-12"
              disabled={!!disabled}
              placeholder={placeholder}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <ComboboxOptions
          transition
          anchor="bottom start"
          className="bg-background-secondary mt-1 space-y-2 p-1 w-(--input-width) [--anchor-max-height:15rem] overflow-y-auto border rounded-xs border-grey-t-800 origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-text-subtle">
              No results
            </div>
          ) : (
            filtered.map((opt) => (
              <ComboboxOption
                key={opt.value}
                value={opt}
                className="z-60 px-3 py-2.5 text-left data-focus:bg-grey-t-800 hover:bg-grey-t-800 rounded-xs text-sm w-full cursor-pointer"
              >
                {opt.label}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </HComboBox>
      {error && (
        <div className="flex space-x-1.5 items-start mt-2">
          <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-0.75" />
          <span className="text-body-smallest text-red-300">{error}</span>
        </div>
      )}
    </div>
  );
}

interface Props<T extends string> {
  options: Array<Option<T>>;
  selected: Array<Option<T>>;
  onSelect: (value: Option<T>) => string | void;
  label?: string;
  disabled?: boolean;
  error?: string;
  OptionIconStart?: HeroIconType;
  isFreeInput?: boolean;
  helperText?: string;
}

export function ComboxBox<T extends string>({
  options,
  selected,
  onSelect,
  label,
  disabled,
  error,
  OptionIconStart,
  isFreeInput,
  helperText,
}: Props<T>) {
  const [value, setValue] = useState('');

  return (
    <div>
      {!!label && (
        <label
          htmlFor={label}
          className={`${
            helperText ? '' : 'mb-2'
          } font-semibold text-body-medium block`}
        >
          {label}
        </label>
      )}
      {!!helperText && (
        <span className="mb-2 mt-1 text-text-low-priority text-sm block">
          {helperText}
        </span>
      )}
      <HComboBox>
        <div
          className={classNames(
            'group -outline-offset-1 flex flex-col border relative rounded-xs overflow-hidden border-grey-t-600 transition-all duration-100  focus-within:outline-white focus-within:outline-2 bg-background-secondary',
            {
              'border-red-300': error?.length,
              'border-grey-t-700 text-text-disabled': disabled,
              'hover:border-white': !disabled,
            }
          )}
        >
          <div className="relative flex">
            {isFreeInput ? (
              <button
                onClick={() => {
                  onSelect({ label: value, value } as Option<T>);

                  setValue('');
                }}
                disabled={value.length === 0}
                className="absolute top-1/2 transform right-0 -translate-y-1/2 p-3 cusor-pointer"
              >
                <PlusIcon className="size-5" />
              </button>
            ) : (
              <ComboboxButton className="cursor-pointer group absolute top-1/2 transform right-0 -translate-y-1/2 p-3">
                <ChevronDownIcon
                  className={classNames(
                    'size-5 group-data-open:rotate-180 transition-all',
                    {
                      'stroke-text-disabled': disabled,
                    }
                  )}
                />
              </ComboboxButton>
            )}
            <ComboboxInput
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (value) {
                    const error = onSelect({
                      label: value,
                      value,
                    } as Option<T>);

                    // If there is an error we want to stop it from clearing the input
                    if (error) {
                      e.preventDefault();
                    }
                  }
                }
              }}
              className="w-full rounded outline-none disabled:text-text-disabled placeholder:text-text-subtle py-3 leading-none px-3 h-10 pr-12"
              disabled={!!disabled}
              placeholder={
                isFreeInput
                  ? 'Type a value and press the enter key'
                  : 'Start typing...'
              }
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>
        <ComboboxOptions
          transition
          anchor="bottom start"
          className="bg-background-secondary mt-1 space-y-2 p-1 w-(--input-width) border rounded-xs border-grey-t-800 origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
          hidden={options.length === 0}
        >
          {options.map((opt) => (
            <ComboboxOption
              value={opt}
              onClick={() => onSelect(opt)}
              className="z-60 px-3 py-2.5 text-left hover:bg-grey-t-800 rounded-xs text-sm w-full"
            >
              {opt.label}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </HComboBox>
      {error && (
        <div className="flex space-x-1.5 items-start mt-2">
          <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
          <span className="text-body-smallest text-red-300">{error}</span>
        </div>
      )}
      <div
        className="space-y-2 mt-2 data-visible:block hidden"
        data-visible={selected.length ? true : undefined}
      >
        {!!selected.length &&
          selected.map((s) => (
            <div
              key={s.value}
              className="flex items-center space-x-1 bg-grey-t-900 w-fit border border-grey-500 rounded-xs"
            >
              <div className="pl-2.5 py-2 space-x-2 flex items-center">
                {OptionIconStart && <OptionIconStart className="size-4" />}
                <span>{s.label}</span>
              </div>
              <IconButton onClick={() => onSelect(s)} Icon={XMarkIcon} />
            </div>
          ))}
      </div>
    </div>
  );
}
